import axios from "axios";
import APP_CONFIG from "../app/configuration/AppConfig";
import type { ProductType } from "../purchasedProducts/Models";

export interface SiteGroup {
  id: number;
  name: string;
  customerId: number;
  siteIds: string[];
  createdAtUtc: string;
  updatedAtUtc: string;
  updatedByUserId: string;
  isAdminOnly: boolean;
  productOverride: ProductType | null;
}

export interface SiteGroupMemberChoice {
  siteId: string;
  name: string;
  customerId: number;
  customerName: string | null;
  timeZone: string | null;
  latitude: number | null;
  longitude: number | null;
  products: number[];
}

const url = `${APP_CONFIG.serviceUrls.apiHostUrl}/site-groups`;

export const validateSiteGroupMembers = async (
  siteIds: string[],
  signal: AbortSignal,
  isAdminOnly = false,
  productOverride: ProductType | null = null
): Promise<string[]> =>
  (
    await axios.post<string[]>(
      `${url}/validate-members`,
      { siteIds, isAdminOnly, productOverride },
      { signal, timeout: 30_000 }
    )
  ).data;

export const getSiteGroupWarnings = async (signal: AbortSignal): Promise<Record<string, string[]>> =>
  (await axios.get<Record<string, string[]>>(`${url}/validation-warnings`, { signal, timeout: 30_000 }))
    .data;

export const getSiteGroups = async (signal?: AbortSignal): Promise<SiteGroup[]> =>
  (await axios.get<SiteGroup[]>(url, { signal, timeout: 30_000 })).data;

export const getSiteGroup = async (id: number, signal?: AbortSignal): Promise<SiteGroup> =>
  (await axios.get<SiteGroup>(`${url}/${encodeURIComponent(id)}`, { signal })).data;

export const getSiteGroupMembers = async (signal?: AbortSignal): Promise<SiteGroupMemberChoice[]> =>
  (await axios.get<SiteGroupMemberChoice[]>(`${url}/member-choices`, { signal })).data;

export const saveSiteGroup = async (
  id: number | undefined,
  name: string,
  siteIds: string[],
  isAdminOnly = false,
  productOverride: ProductType | null = null
) => {
  const body = { name, siteIds, isAdminOnly, productOverride };
  return (id ? await axios.put<SiteGroup>(`${url}/${id}`, body) : await axios.post<SiteGroup>(url, body))
    .data;
};

export const deleteSiteGroup = async (id: number) => axios.delete(`${url}/${id}`);

export const siteGroupError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if (typeof error.response?.data?.detail === "string") {
      return error.response.data.detail;
    }
    if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
      return "The request timed out. Try again shortly.";
    }
  }
  return "The request failed. Try again shortly.";
};
